import type { ReactNode } from 'react';
import type { AiMessagePart, RenderPartFn } from 'juneau';
import { AiProposalCard, isChartPart, isProposalPart, useAiChatContext } from 'juneau';
import { InvoiceCardPart } from './invoice-card/InvoiceCardPart';
import { InvoiceCollectionPart } from './invoice-card/InvoiceCollectionPart';
import { isInvoiceCardPart, isInvoiceCollectionPart } from './invoice-card/invoice-card.types';
import { ActivityPart, isActivityPart } from './activity/ActivityPart';
import { EntityCardPart, EntityListPart } from './entity/EntityCardPart';
import { IssueCollectionPart } from './entity/IssueCollectionPart';
import { isEntityListPart, isEntityPart, isIssueCollectionPart } from './entity/entity-part.types';
import { ChartPart } from './chart/ChartPart';
import { AggregateSummaryPart } from './aggregate-summary/AggregateSummaryPart';
import { isAggregateSummaryPart } from './aggregate-summary/aggregate-summary.types';
import { InvoiceDetailPart } from './invoice-detail/InvoiceDetailPart';
import { isInvoiceDetailPart } from './invoice-detail/invoice-detail.types';
import { AddPartnersProposalCard } from './proposal/AddPartnersProposalCard';
import { MoveInvoicesStatusProposalCard } from './proposal/MoveInvoicesStatusProposalCard';
import { MaintainInvoicesProposalCard } from './proposal/MaintainInvoicesProposalCard';
import { InviteUserProposalCard } from './proposal/InviteUserProposalCard';
import { InviteUsersProposalCard } from './proposal/InviteUsersProposalCard';
import { MaintainPartnersProposalCard } from './proposal/MaintainPartnersProposalCard';
import { MoveProcurementStatusProposalCard } from './proposal/MoveProcurementStatusProposalCard';
import { RetrySyncsProposalCard } from './proposal/RetrySyncsProposalCard';
import { SubmitProcurementProposalCard } from './proposal/SubmitProcurementProposalCard';
import {
  readInviteEmails,
  readInviteOrgUnitLabel,
  readInviteUser,
  readMaintainInvoices,
  readMaintainPartners,
  readMoveInvoices,
  readMoveProcurement,
  readPartnerRows,
  readRetrySyncs,
  readSubmitProcurement
} from './proposal/proposal-part.types';
import { ChatCommandProposalCard } from './proposal/ChatCommandProposalCard';
import { ChatChangeProposalCard } from './proposal/ChatChangeProposalCard';
import {
  readChatChange,
  readChatCommand,
  readChatSecretFields
} from './proposal/chat-proposal.types';
import { NudgePart } from '../nudges/NudgePart';
import { isNudgePart } from '../nudges/nudge.types';
import type { ChatCardActionType } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/**
 * Proposal action types the UI can label. Keys are the `title` emitted by the backend
 * proposal tools; values are static so translation keys stay statically verifiable.
 */
const PROPOSAL_LABEL_KEYS = {
  'invite-user': 'proposals.invite-user',
  'approve-invoice': 'proposals.approve-invoice',
  'reject-invoice': 'proposals.reject-invoice',
  'revert-invoice': 'proposals.revert-invoice',
  'move-invoice-status': 'proposals.move-invoice-status',
  'add-partners': 'proposals.add-partners',
  'move-invoices-status': 'proposals.move-invoices-status',
  'maintain-invoices': 'proposals.maintain-invoices',
  'invite-users': 'proposals.invite-users',
  'maintain-partners': 'proposals.maintain-partners',
  'move-requisitions-status': 'proposals.move-procurement.requisitions',
  'move-purchase-orders-status': 'proposals.move-procurement.purchaseOrders',
  'submit-requisitions': 'proposals.submit-procurement.requisitions',
  'submit-purchase-orders': 'proposals.submit-procurement.purchaseOrders',
  'retry-syncs': 'proposals.retry-syncs'
} as const;

type ProposalActionType = keyof typeof PROPOSAL_LABEL_KEYS;

/** Titles of the catalog command and change cards; values stay static for key verification. */
const CHAT_CARD_TITLE_KEYS = {
  'cancel-requisitions': 'proposals.chat.titles.cancel-requisitions',
  'complete-requisitions': 'proposals.chat.titles.complete-requisitions',
  'archive-requisitions': 'proposals.chat.titles.archive-requisitions',
  'unarchive-requisitions': 'proposals.chat.titles.unarchive-requisitions',
  'return-requisitions-to-draft': 'proposals.chat.titles.return-requisitions-to-draft',
  'accept-requisition-warnings': 'proposals.chat.titles.accept-requisition-warnings',
  'delete-requisitions': 'proposals.chat.titles.delete-requisitions',
  'cancel-purchase-orders': 'proposals.chat.titles.cancel-purchase-orders',
  'complete-purchase-orders': 'proposals.chat.titles.complete-purchase-orders',
  'archive-purchase-orders': 'proposals.chat.titles.archive-purchase-orders',
  'unarchive-purchase-orders': 'proposals.chat.titles.unarchive-purchase-orders',
  'return-purchase-orders-to-draft': 'proposals.chat.titles.return-purchase-orders-to-draft',
  'accept-purchase-order-warnings': 'proposals.chat.titles.accept-purchase-order-warnings',
  'delete-purchase-orders': 'proposals.chat.titles.delete-purchase-orders',
  'draft-requisition': 'proposals.chat.titles.draft-requisition',
  'draft-purchase-order': 'proposals.chat.titles.draft-purchase-order',
  'order-from-requisition': 'proposals.chat.titles.order-from-requisition',
  'dispatch-purchase-order': 'proposals.chat.titles.dispatch-purchase-order',
  'confirm-goods-receipts': 'proposals.chat.titles.confirm-goods-receipts',
  'cancel-goods-receipts': 'proposals.chat.titles.cancel-goods-receipts',
  'confirm-delivery-without-receipt': 'proposals.chat.titles.confirm-delivery-without-receipt',
  'archive-goods-receipts': 'proposals.chat.titles.archive-goods-receipts',
  'unarchive-goods-receipts': 'proposals.chat.titles.unarchive-goods-receipts',
  'delete-goods-receipts': 'proposals.chat.titles.delete-goods-receipts',
  'receive-from-purchase-order': 'proposals.chat.titles.receive-from-purchase-order',
  'draft-goods-receipt': 'proposals.chat.titles.draft-goods-receipt',
  'reprocess-documents': 'proposals.chat.titles.reprocess-documents',
  'delete-documents': 'proposals.chat.titles.delete-documents',
  'release-quarantined-emails': 'proposals.chat.titles.release-quarantined-emails',
  'discard-quarantined-emails': 'proposals.chat.titles.discard-quarantined-emails',
  'untrust-senders': 'proposals.chat.titles.untrust-senders',
  'trust-senders': 'proposals.chat.titles.trust-senders',
  'manage-mailbox': 'proposals.chat.titles.manage-mailbox',
  'unlink-invoices': 'proposals.chat.titles.unlink-invoices',
  'link-invoices': 'proposals.chat.titles.link-invoices',
  'remove-invoice-attachments': 'proposals.chat.titles.remove-invoice-attachments',
  'edit-invoice': 'proposals.chat.titles.edit-invoice',
  'code-invoices': 'proposals.chat.titles.code-invoices',
  'deactivate-cost-centers': 'proposals.chat.titles.deactivate-cost-centers',
  'draft-cost-center': 'proposals.chat.titles.draft-cost-center',
  'activate-jobs': 'proposals.chat.titles.activate-jobs',
  'suspend-jobs': 'proposals.chat.titles.suspend-jobs',
  'close-jobs': 'proposals.chat.titles.close-jobs',
  'delete-jobs': 'proposals.chat.titles.delete-jobs',
  'draft-job': 'proposals.chat.titles.draft-job',
  'draft-product': 'proposals.chat.titles.draft-product',
  'deactivate-products': 'proposals.chat.titles.deactivate-products',
  'delete-products': 'proposals.chat.titles.delete-products',
  'draft-org-unit': 'proposals.chat.titles.draft-org-unit',
  'deactivate-org-units': 'proposals.chat.titles.deactivate-org-units',
  'change-org-unit-members': 'proposals.chat.titles.change-org-unit-members',
  'set-org-unit-manager': 'proposals.chat.titles.set-org-unit-manager',
  'delete-partners': 'proposals.chat.titles.delete-partners',
  'change-number-series': 'proposals.chat.titles.change-number-series',
  'change-approval-policy': 'proposals.chat.titles.change-approval-policy',
  'reset-approval-policy': 'proposals.chat.titles.reset-approval-policy',
  'change-automation-rules': 'proposals.chat.titles.change-automation-rules',
  'change-validation-policy': 'proposals.chat.titles.change-validation-policy',
  'reset-validation-policy': 'proposals.chat.titles.reset-validation-policy',
  'change-invoice-automation-switches': 'proposals.chat.titles.change-invoice-automation-switches',
  'change-document-output': 'proposals.chat.titles.change-document-output',
  'delete-email-templates': 'proposals.chat.titles.delete-email-templates',
  'rerun-analysis': 'proposals.chat.titles.rerun-analysis',
  'confirm-matches': 'proposals.chat.titles.confirm-matches',
  'reject-matches': 'proposals.chat.titles.reject-matches',
  'resend-sync-changes': 'proposals.chat.titles.resend-sync-changes',
  'retry-stuck-sync-events': 'proposals.chat.titles.retry-stuck-sync-events',
  'repush-attachments': 'proposals.chat.titles.repush-attachments',
  'buy-credit-pack': 'proposals.chat.titles.buy-credit-pack',
  'setup-connector': 'proposals.chat.titles.setup-connector',
  'switch-connector': 'proposals.chat.titles.switch-connector',
  'change-connector-schedules': 'proposals.chat.titles.change-connector-schedules',
  'change-connector-settings': 'proposals.chat.titles.change-connector-settings',
  'delete-connector': 'proposals.chat.titles.delete-connector',
  'delete-connection': 'proposals.chat.titles.delete-connection',
  'accept-invoice-warnings': 'proposals.chat.titles.accept-invoice-warnings',
  'approve-invoices-accepting-warnings':
    'proposals.chat.titles.approve-invoices-accepting-warnings',
  'submit-invoices': 'proposals.chat.titles.submit-invoices',
  'return-invoices-to-draft': 'proposals.chat.titles.return-invoices-to-draft',
  'cancel-invoices': 'proposals.chat.titles.cancel-invoices',
  'delete-invoices': 'proposals.chat.titles.delete-invoices',
  'complete-invoices': 'proposals.chat.titles.complete-invoices',
  'unmatch-invoice-lines': 'proposals.chat.titles.unmatch-invoice-lines',
  'reject-match-links': 'proposals.chat.titles.reject-match-links',
  'rematch-invoices': 'proposals.chat.titles.rematch-invoices',
  'pair-invoice-lines': 'proposals.chat.titles.pair-invoice-lines',
  'set-approvers': 'proposals.chat.titles.set-approvers',
  'move-invoices-to-export': 'proposals.chat.titles.move-invoices-to-export',
  'mark-notifications-read': 'proposals.chat.titles.mark-notifications-read',
  'reply-to-thread': 'proposals.chat.titles.reply-to-thread'
} as const satisfies Record<ChatCardActionType, string>;

function chatCardTitleKey(actionType: string) {
  return Object.prototype.hasOwnProperty.call(CHAT_CARD_TITLE_KEYS, actionType)
    ? CHAT_CARD_TITLE_KEYS[actionType as keyof typeof CHAT_CARD_TITLE_KEYS]
    : null;
}

function isKnownActionType(value: string): value is ProposalActionType {
  return Object.prototype.hasOwnProperty.call(PROPOSAL_LABEL_KEYS, value);
}

function ProposalPart({ part }: { part: AiMessagePart }) {
  const t = useFeatureTranslation('aiChat');
  const { confirmProposal, cancelProposal } = useAiChatContext();
  if (!isProposalPart(part)) return null;

  const actionType = part.proposal.title;
  // The cards report only the id; juneau also takes the payload, as its own card passes it.
  const confirm = (proposalId: string) => confirmProposal(proposalId, part.proposal.payload);

  // Catalog cards render from the payload shape; an unlabelled one has drifted and renders nothing.
  const chatTitleKey = chatCardTitleKey(actionType);
  if (chatTitleKey !== null) {
    const command = readChatCommand(part.proposal.payload);
    if (command) {
      return (
        <ChatCommandProposalCard
          title={t(chatTitleKey)}
          proposalId={part.proposal.id}
          payload={command}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
    const change = readChatChange(part.proposal.payload);
    if (change) {
      return (
        <ChatChangeProposalCard
          title={t(chatTitleKey)}
          proposalId={part.proposal.id}
          payload={change}
          secretFields={readChatSecretFields(part.proposal.payload)}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
    return null;
  }

  // An unknown action type means backend and frontend have drifted — render nothing
  // rather than leaking a raw slug into the UI.
  if (!isKnownActionType(actionType)) return null;

  // Bulk actions get a card that lists every row: the user must see exactly what they
  // confirm, which the generic payload preview cannot show for a long list.
  if (actionType === 'add-partners') {
    const rows = readPartnerRows(part.proposal.payload);
    if (rows) {
      return (
        <AddPartnersProposalCard
          proposalId={part.proposal.id}
          rows={rows}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
  }

  if (actionType === 'move-invoices-status') {
    const transition = readMoveInvoices(part.proposal.payload);
    if (transition) {
      return (
        <MoveInvoicesStatusProposalCard
          proposalId={part.proposal.id}
          payload={transition}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
  }

  if (actionType === 'maintain-invoices') {
    const maintenance = readMaintainInvoices(part.proposal.payload);
    if (maintenance) {
      return (
        <MaintainInvoicesProposalCard
          proposalId={part.proposal.id}
          payload={maintenance}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
  }

  if (actionType === 'invite-user') {
    const invite = readInviteUser(part.proposal.payload);
    if (invite) {
      return (
        <InviteUserProposalCard
          proposalId={part.proposal.id}
          payload={invite}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
  }

  if (actionType === 'invite-users') {
    const emails = readInviteEmails(part.proposal.payload);
    if (emails) {
      return (
        <InviteUsersProposalCard
          proposalId={part.proposal.id}
          emails={emails}
          orgUnitLabel={readInviteOrgUnitLabel(part.proposal.payload)}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
  }

  if (actionType === 'maintain-partners') {
    const maintenance = readMaintainPartners(part.proposal.payload);
    if (maintenance) {
      return (
        <MaintainPartnersProposalCard
          proposalId={part.proposal.id}
          payload={maintenance}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
  }

  if (actionType === 'retry-syncs') {
    const rows = readRetrySyncs(part.proposal.payload);
    if (rows) {
      return (
        <RetrySyncsProposalCard
          proposalId={part.proposal.id}
          rows={rows}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
  }

  if (actionType === 'move-requisitions-status' || actionType === 'move-purchase-orders-status') {
    const transition = readMoveProcurement(part.proposal.payload);
    if (transition) {
      return (
        <MoveProcurementStatusProposalCard
          proposalId={part.proposal.id}
          payload={transition}
          kind={actionType === 'move-requisitions-status' ? 'requisitions' : 'purchaseOrders'}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
  }

  if (actionType === 'submit-requisitions' || actionType === 'submit-purchase-orders') {
    const submission = readSubmitProcurement(part.proposal.payload);
    if (submission) {
      return (
        <SubmitProcurementProposalCard
          proposalId={part.proposal.id}
          payload={submission}
          kind={actionType === 'submit-requisitions' ? 'requisitions' : 'purchaseOrders'}
          resolved={part.proposal.resolved}
          onConfirm={confirm}
          onCancel={cancelProposal}
        />
      );
    }
  }

  const baseKey = PROPOSAL_LABEL_KEYS[actionType];
  const title = t(`${baseKey}.title`);
  const description = t(`${baseKey}.description`);

  return (
    <AiProposalCard
      part={{
        ...part,
        proposal: { ...part.proposal, title, description }
      }}
      onConfirm={confirmProposal}
      onCancel={cancelProposal}
    />
  );
}

interface PartRenderer {
  type: string;
  render: (part: AiMessagePart) => ReactNode;
}

const renderers: PartRenderer[] = [
  {
    type: 'invoice-card',
    render: part => (isInvoiceCardPart(part) ? <InvoiceCardPart part={part} /> : null)
  },
  {
    // Stands in for a whole invoice result — a long list would flood the panel with cards.
    type: 'invoice-collection',
    render: part => (isInvoiceCollectionPart(part) ? <InvoiceCollectionPart part={part} /> : null)
  },
  {
    // Overrides juneau's built-in activity renderer with i18n-owned labels.
    type: 'activity',
    render: part => (isActivityPart(part) ? <ActivityPart part={part} /> : null)
  },
  {
    // Overrides juneau's built-in entity card with the Tappeer design system.
    type: 'entity',
    render: part => (isEntityPart(part) ? <EntityCardPart part={part} /> : null)
  },
  {
    // Overrides juneau's built-in entity list with the Tappeer design system.
    type: 'entity-list',
    render: part => (isEntityListPart(part) ? <EntityListPart part={part} /> : null)
  },
  {
    // Stands in for every validation issue of one invoice — a long list buries the answer.
    type: 'issue-collection',
    render: part => (isIssueCollectionPart(part) ? <IssueCollectionPart part={part} /> : null)
  },
  {
    type: 'aggregate-summary',
    render: part => (isAggregateSummaryPart(part) ? <AggregateSummaryPart part={part} /> : null)
  },
  {
    // Renders the juneau chart schema with the Tappeer ChartCard (D3).
    type: 'chart',
    render: part => (isChartPart(part) ? <ChartPart part={part} /> : null)
  },
  {
    type: 'invoice-detail',
    render: part => (isInvoiceDetailPart(part) ? <InvoiceDetailPart part={part} /> : null)
  },
  {
    // A proactive offer to start a guided set-up — closes a greeting or follows a signal.
    type: 'nudge',
    render: part => (isNudgePart(part) ? <NudgePart part={part} /> : null)
  },
  {
    // Overrides juneau's built-in proposal card with i18n-owned title and description.
    type: 'proposal',
    render: part => <ProposalPart part={part} />
  }
];

export const renderPart: RenderPartFn = part => {
  const renderer = renderers.find(r => r.type === part.type);
  return renderer?.render(part) ?? null;
};
